<template>
  <div
    :class="`p-${paddingSize}`"
  >
    <div class="field-wrapper mb-0">
      <div class="flex flex-column">
        <div class="flex flex-column">
          <div class="flex gap-1">
            <div
              class="flex flex-column gap-1 segment-row-field"
              :class="[size === 'small' ? 'xs:flex-row' : 'md:flex-row']"
            >
              <div class="segment-row-field">
                <span
                  v-if="readonly && selectedProperty"
                  class="font-semibold text-sm"
                >{{ getSelectedOptionLabel(selectedProperty) }}</span>
                <Dropdown
                  v-else-if="showEditForm"
                  ref="dropdownRef"
                  v-model="selectedProperty"
                  filter
                  :options="propertiesOptions"
                  option-label="label"
                  option-group-label="label"
                  option-value="value"
                  option-group-children="items"
                  :placeholder="t('segmentations.labels.selectSegment')"
                  class="w-full"
                  @change="onChangeProperty"
                  @before-show="onBeforeShow"
                >
                  <template #option="slotProps">
                    <div
                      v-tooltip="slotProps.option.isCustomAttribute ? t('segmentation.custom.tooltip') : null"
                      class="flex align-items-center justify-content-between w-full"
                    >
                      <div class="flex align-items-center">
                        <i
                          v-if="slotProps.option.isCustomAttribute"
                          class="fa-solid fa-puzzle-piece custom-icon mr-2"
                        />
                        <i
                          v-if="isPropertyOverridden(slotProps.option.value)"
                          class="fa-solid fa-layer-group override-icon mr-2"
                        />
                        <span>{{ slotProps.option.label }}</span>
                      </div>
                      <div class="flex align-items-center gap-1">
                        <span
                          v-if="slotProps.option.isIteration"
                          class="iteration-badge"
                        >ITERATION</span>
                        <span
                          v-if="slotProps.option.isMetadata"
                          class="metadata-badge"
                        >METADATA</span>
                        <span
                          v-if="slotProps.option.isCustomAttribute"
                          class="custom-badge"
                        >CUSTOM</span>
                        <span
                          v-if="isPropertyOverridden(slotProps.option.value)"
                          class="override-badge"
                        >OVERRIDE</span>
                        <i
                          v-if="isPropertyOverridden(slotProps.option.value)"
                          class="fa-regular fa-circle-question help-icon"
                          @click.stop="(e) => openOverridePanel(e, slotProps.option.value)"
                        />
                      </div>
                    </div>
                  </template>
                  <template #value="slotProps">
                    <div
                      v-if="slotProps.value"
                      class="flex align-items-center justify-content-between w-full"
                    >
                      <div class="flex align-items-center">
                        <i
                          v-if="getSelectedOptionIsCustom(slotProps.value)"
                          class="fa-solid fa-puzzle-piece custom-icon mr-2"
                        />
                        <i
                          v-if="isPropertyOverridden(slotProps.value)"
                          class="fa-solid fa-layer-group override-icon mr-2"
                        />
                        <span>{{ getSelectedOptionLabel(slotProps.value) }}</span>
                      </div>
                      <div class="flex align-items-center gap-1">
                        <span
                          v-if="getSelectedOptionIsIteration(slotProps.value)"
                          class="iteration-badge-small"
                        >ITERATION</span>
                        <span
                          v-if="getSelectedOptionIsMetadata(slotProps.value)"
                          class="metadata-badge-small"
                        >METADATA</span>
                        <span
                          v-if="getSelectedOptionIsCustom(slotProps.value)"
                          class="custom-badge-small"
                        >CUSTOM</span>
                        <span
                          v-if="isPropertyOverridden(slotProps.value)"
                          class="override-badge-small"
                        >OVERRIDE</span>
                        <i
                          v-if="isPropertyOverridden(slotProps.value)"
                          class="fa-regular fa-circle-question help-icon-small"
                          @click.stop="(e) => openOverridePanel(e, slotProps.value)"
                        />
                      </div>
                    </div>
                    <span
                      v-else
                      class="dropdown-placeholder"
                    >{{ t('segmentations.labels.selectSegment') }}</span>
                  </template>
                </Dropdown>
                <div
                  v-if="errorLevel && errorLevel==='property'"
                  class="mt-1"
                >
                  <small
                    class="p-error"
                  >
                    {{ t('segmentation.propErrorMsg') }}
                  </small>
                </div>
              </div>
              <SegmentationSimpleProperty
                v-if="selectedPropertyDefinition && ('logical_operators' in selectedPropertyDefinition || 'options' in selectedPropertyDefinition)"
                :key="`simple-property-${refreshPropertyDefinition}`"
                v-model:property-definition="selectedPropertyDefinition"
                v-model:property-config="blockOr"
                :field-id="selectedPropertyDefinition.id"
                :size="size"
                :available-contexts="availableContexts"
                :readonly="readonly"
              />
            </div>
            <div
              v-if="!readonly"
              class="ml-auto flex"
            >
              <PropertyDeletePanel
                :title="t('segmentation.orDeleteTitle')"
                type="blockOR"
                @remove-callback="deleteBlockOR"
              />
            </div>
          </div>
          <div
            v-if="selectedPropertyDefinition && 'sub_properties' in selectedPropertyDefinition"
            class="flex mt-2"
          >
            <SegmentationComplexProperty
              :key="`complex-property-${refreshPropertyDefinition}`"
              v-model:property-definition="selectedPropertyDefinition"
              v-model:property-config="blockOr"
              :level="level"
              :size="size"
              :available-contexts="availableContexts"
              :readonly="readonly"
              @refresh-component="refreshPropertyComponent"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
  <OverlayPanel ref="overridePanelRef">
    <OverrideInfoPanel
      v-if="currentOverrideField"
      :system-field-name="currentOverrideField"
    />
  </OverlayPanel>
</template>

<script lang="ts">
import {
  defineComponent, onMounted, ref, watch, computed,
} from 'vue';
import Dropdown from 'primevue/dropdown';
import Tooltip from 'primevue/tooltip';
import OverlayPanel from 'primevue/overlaypanel';
import { useI18n } from 'vue-i18n';
import SegmentationSimpleProperty from '@/components/segmentations/SegmentationSimpleProperty.vue';
import SegmentationComplexProperty from '@/components/segmentations/SegmentationComplexProperty.vue';
import OverrideInfoPanel from '@/components/segmentations/OverrideInfoPanel.vue';
import { store } from '@/store';
import PropertyDeletePanel from '@/components/segmentations/PropertyDeletePanel.vue';
import validateSegmentProperty from '@/components/segmentations/utils/utils';
import useProductOverrides from '@/composables/segmentations/useProductOverrides';
import { TemplateEditorState } from '@/composables/template-editor/TemplateEditor';
import { TemplateParentTypeEnum } from '@/types';

export default defineComponent({
  name: 'SegmentationBlockOR',

  components: {
    PropertyDeletePanel,
    Dropdown,
    SegmentationSimpleProperty,
    SegmentationComplexProperty,
    OverlayPanel,
    OverrideInfoPanel,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    blockOr: {
      type: Object,
      required: true,
    },

    indexBlockAnd: {
      type: Number,
      required: true,
    },

    indexBlockOr: {
      type: Number,
      required: true,
    },

    lengthBlockAnd: {
      type: Number,
      required: true,
    },

    size: {
      type: String,
      required: false,
      default: '',
    },

    // Optional theme to force specific context (e.g., 'product' for HTML blocks in product loops)
    theme: {
      type: String,
      required: false,
      default: '',
    },

    readonly: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  setup(props) {
    const { t } = useI18n();
    // Use theme prop if provided, otherwise use blockOr.theme
    const selectedTheme = ref(props.theme || props.blockOr.theme);
    const refreshPropertyDefinition = ref(0);
    const level = ref(1);
    const errorLevel = ref();
    // Initialize isConditionEditing based on whether the condition is complete

    // Determine available contexts based on theme and template type
    const availableContexts = computed(() => {
      const contexts: string[] = [];

      // Add contact context if theme is 'contacts' or not specified (default)
      if (!selectedTheme.value || selectedTheme.value === 'contacts' || selectedTheme.value === 'contact') {
        contexts.push('contact');
        // Contact theme also includes order and cart contexts
        contexts.push('order', 'cart');
      }

      // Add product context if theme is 'products'
      if (selectedTheme.value === 'products' || selectedTheme.value === 'product') {
        contexts.push('product');
      }

      // Add visitor context only for popup and embed (smart content on site) templates
      const templateType = TemplateEditorState.template?.type;
      if (templateType === TemplateParentTypeEnum.POPUP || templateType === TemplateParentTypeEnum.EMBED) {
        contexts.push('visitor');
      }

      return contexts;
    });

    const selectedProperty = ref(props.blockOr.id ? props.blockOr.id : null);
    const propertiesOptions: any = ref([]);
    const selectedPropertyDefinition = ref<any>({});

    const selectedPropertyGroup = ref(null);
    const overridePanelRef = ref();
    const currentOverrideField = ref<string | null>(null);
    const { loadOverrides, hasOverrides } = useProductOverrides();

    const refreshPropertyComponent = () => {
      refreshPropertyDefinition.value += 1;
    };

    const init = () => {
      const groupProperties: any = store.getters['segmentationsEditor/getGroupProperties']();
      propertiesOptions.value = Object.entries(groupProperties).map(([key, values]) => {
        const groupData = values as any;
        const rawLabel = groupData.rawLabel || false;
        const items = groupData.list || [];
        const mapped = items.map((value: any) => ({
          label: value.rawLabel ? value.label : t(value.label),
          value: value.id,
          isCustomAttribute: value.isCustomAttribute || false,
          isIteration: value.isIteration || false,
          isMetadata: value.isMetadata || false,
        }));
        return {
          label: rawLabel ? groupData.label : t(`segmentations.${key}.label`),
          code: key,
          items: mapped,
        };
      });

      if (selectedProperty.value === null) {
        selectedPropertyDefinition.value = {};
      }

      refreshPropertyComponent();
    };

    const onChangeProperty = () => {
      if (selectedProperty.value) {
        const group = propertiesOptions.value.find((groupItem: any) => groupItem.items.some((item: any) => item.value === selectedProperty.value));
        selectedPropertyGroup.value = group ? group.code : null;
        selectedPropertyDefinition.value = store.getters['segmentationsEditor/getPropertyDefinition'](selectedPropertyGroup.value, selectedProperty.value);
        store.commit('segmentationsEditor/removePropertyError', `${props.indexBlockAnd}-${props.indexBlockOr}`);
      }
      refreshPropertyComponent();
    };

    const deleteBlockOR = () => {
      const { indexBlockAnd, indexBlockOr } = props;
      store.commit('segmentationsEditor/removeBlockOR', {
        indexBlockAND: indexBlockAnd,
        indexBlockOR: indexBlockOr,
      });
    };

    const currentSegmentConfig = computed(() => store.getters['segmentationsEditor/getCurrentSegmentConfig']);

    // Helper function to check if selected option is custom
    const getSelectedOptionIsCustom = (value: string) => propertiesOptions.value.reduce((isCustom: boolean, group: any) => {
      const option = group.items.find((item: any) => item.value === value);
      return isCustom || (option?.isCustomAttribute || false);
    }, false);

    // Helper function to check if selected option is an iteration segment
    const getSelectedOptionIsIteration = (value: string) => propertiesOptions.value.reduce((isIteration: boolean, group: any) => {
      const option = group.items.find((item: any) => item.value === value);
      return isIteration || (option?.isIteration || false);
    }, false);

    // Helper function to check if selected option is metadata
    const getSelectedOptionIsMetadata = (value: string) => propertiesOptions.value.reduce((isMeta: boolean, group: any) => {
      const option = group.items.find((item: any) => item.value === value);
      return isMeta || (option?.isMetadata || false);
    }, false);

    // Helper function to get selected option label
    const getSelectedOptionLabel = (value: string) => propertiesOptions.value.reduce((label: string, group: any) => {
      const option = group.items.find((item: any) => item.value === value);
      return option ? option.label || '' : label;
    }, '');

    const showEditForm = computed(() => {
      // Never show edit form in readonly mode
      if (props.readonly) {
        return false;
      }
      if (!Object.keys(props.blockOr).length
      || (currentSegmentConfig.value && currentSegmentConfig.value.configProperty.segmentId === props.blockOr.segmentId) || (props.blockOr.sub_properties)
      ) {
        return true;
      }
      // Bare segment: the dropdown is the whole configuration — keep it
      // visible once collapsed, nothing else would render.
      if (
        selectedPropertyDefinition.value
        && !selectedPropertyDefinition.value.sub_properties
        && !selectedPropertyDefinition.value.logical_operators
        && !selectedPropertyDefinition.value.options
      ) {
        return true;
      }
      return false;
    });

    const paddingSize = computed(() => (props.size === 'small' ? '1' : '2'));

    /**
     * Check if a property is overridden
     *
     * @param propertyId - The ID of the property to check
     * @returns true if the property is overridden, false otherwise
     */
    const isPropertyOverridden = (propertyId: string) => {
      // Get the parent context from selectedPropertyGroup
      const parentContextId = selectedPropertyGroup.value || undefined;
      return hasOverrides(propertyId, parentContextId);
    };

    const openOverridePanel = (event: Event, propertyId: string) => {
      currentOverrideField.value = propertyId;
      overridePanelRef.value.toggle(event);
    };

    watch([selectedProperty], () => {
      const property: any = {
        id: selectedProperty.value,
        groupProperty: selectedPropertyGroup.value,
      };

      // Add isCustomAttribute flag if present in definition
      if (selectedProperty.value && selectedPropertyDefinition.value && selectedPropertyDefinition.value.isCustomAttribute) {
        property.isCustomAttribute = true;
      }

      if (selectedProperty.value && selectedPropertyDefinition.value && 'sub_properties' in selectedPropertyDefinition.value) {
        property.sub_properties = [];
      }
      const { indexBlockAnd, indexBlockOr } = props;

      store.commit('segmentationsEditor/updateSegmentProperty', {
        indexBlockAND: indexBlockAnd,
        indexBlockOR: indexBlockOr,
        segmentData: property,
      });
      if (selectedPropertyDefinition.value && !selectedPropertyDefinition.value.sub_properties) {
        store.commit('segmentationsEditor/setCurrentSegmentConfig', {
          configProperty: property,
          propertyDefinition: selectedPropertyDefinition.value,
        });
      }
      if (selectedPropertyDefinition.value.sub_properties) {
        store.commit('segmentationsEditor/setCurrentSegmentConfig', null);
      }
    });

    watch(() => store.getters['segmentationsEditor/getErrors'], () => {
      const errorsArray = store.getters['segmentationsEditor/getErrors'];
      errorLevel.value = null;
      if (errorLevel.value === null) {
        errorsArray.properties.forEach((errorLevelString: string) => {
          if (errorLevelString === `${props.indexBlockAnd}-${props.indexBlockOr}`) {
            errorLevel.value = 'property';
          }
        });
      }
    }, { deep: true });

    const dropdownRef = ref();

    const onBeforeShow = async () => {
      if (currentSegmentConfig.value) {
        if (((Object.keys(props.blockOr).length && currentSegmentConfig.value.configProperty.segmentId !== props.blockOr.segmentId)) || !Object.keys(props.blockOr).length) {
          const { segmentId } = currentSegmentConfig.value.configProperty;
          const validation = await validateSegmentProperty(currentSegmentConfig.value.configProperty, currentSegmentConfig.value.propertyDefinition);
          if (validation && !validation.success) {
            store.commit('segmentationsEditor/pushError', { type: 'segments', value: { segmentId, value: validation.validate.value } });
            dropdownRef.value.hide();
            return false;
          }
          store.commit('segmentationsEditor/removeError', { type: 'segments', id: 'segmentId', value: segmentId });
          store.commit('segmentationsEditor/setCurrentSegmentConfig', null);
          store.commit('segmentationsEditor/setRefreshContactsNumber', true);
        }
      } else {
        store.commit('segmentationsEditor/setCurrentSegmentConfig', null);
      }
    };

    onMounted(async () => {
      if (!props.readonly) {
        await loadOverrides();
      }
      init();
      onChangeProperty();
    });

    return {
      t,
      deleteBlockOR,
      selectedTheme,
      availableContexts,
      onChangeProperty,
      selectedProperty,
      selectedPropertyDefinition,
      propertiesOptions,
      level,
      errorLevel,
      refreshPropertyDefinition,
      refreshPropertyComponent,
      showEditForm,
      onBeforeShow,
      dropdownRef,
      paddingSize,
      getSelectedOptionIsCustom,
      getSelectedOptionIsIteration,
      getSelectedOptionIsMetadata,
      getSelectedOptionLabel,
      isPropertyOverridden,
      overridePanelRef,
      openOverridePanel,
      currentOverrideField,
      readonly: props.readonly,
    };
  },
});
</script>

<style scoped lang="scss" >
@import '@/assets/styles/custom-properties.scss';

/* Lets the segment selector shrink instead of clipping the delete button. */
.segment-row-field {
  min-width: 0;
  flex: 1 1 auto;
}

.segment-row-field :deep(.p-dropdown-label) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.block-type-toggle {
  display: flex;
  gap: 0.5rem;

  .toggle-button {
    flex: 1;
    transition: all 0.2s ease;

    &.active {
      font-weight: 600;
    }

    &:not(.active) {
      background: white !important;
      color: var(--text-color) !important;
      border: 1px solid var(--surface-300) !important;
    }
  }
}

.condition-placeholder {
  background: var(--surface-50);
  border: 1px dashed var(--surface-300);
  border-radius: 8px;
  min-height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.separator {
  border: none;
  border-top: 3px dotted #dbdbdb;
}

.positionAbsoluteTop {
  position: absolute;
  bottom: 3rem;
  right: 0;
}

.positionAbsoluteBottom {
  position: absolute;
  bottom: 0;
  right: 0;
}

.dropdown-placeholder {
  color: #6b7280;
  font-style: italic;
}

.override-icon {
  color: #f59e0b;
  font-size: 0.875rem;
}

.override-badge {
  background: linear-gradient(135deg, #f59e0b 0%, #f97316 100%);
  color: white;
  font-size: 0.6rem;
  font-weight: 700;
  padding: 0.15rem 0.35rem;
  border-radius: 6px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  flex-shrink: 0;
  margin-left: 0.5rem;
}

.override-badge-small {
  background: linear-gradient(135deg, #f59e0b 0%, #f97316 100%);
  color: white;
  font-size: 0.55rem;
  font-weight: 600;
  padding: 0.1rem 0.25rem;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  flex-shrink: 0;
  margin-left: 0.5rem;
}

.help-icon {
  color: #6b7280;
  font-size: 0.875rem;
  cursor: pointer;
  transition: color 0.2s;

  &:hover {
    color: #f59e0b;
  }
}

.help-icon-small {
  color: #6b7280;
  font-size: 0.75rem;
  cursor: pointer;
  transition: color 0.2s;

  &:hover {
    color: #f59e0b;
  }
}

.iteration-badge {
  background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%);
  color: white;
  font-size: 0.6rem;
  font-weight: 700;
  padding: 0.15rem 0.35rem;
  border-radius: 6px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  flex-shrink: 0;
  margin-left: 0.5rem;
}

.iteration-badge-small {
  background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%);
  color: white;
  font-size: 0.55rem;
  font-weight: 600;
  padding: 0.1rem 0.25rem;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  flex-shrink: 0;
  margin-left: 0.5rem;
}

.metadata-badge-small {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
  font-size: 0.55rem;
  font-weight: 600;
  padding: 0.1rem 0.25rem;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  flex-shrink: 0;
  margin-left: 0.5rem;
}
</style>

<style lang="scss">
/* Badges in dropdown overlay (not scoped because PrimeVue renders overlay outside component) */
.p-dropdown-panel {
  .custom-badge {
    background: linear-gradient(135deg, #8b5cf6 0%, #a855f7 100%);
    color: white;
    font-size: 0.6rem;
    font-weight: 700;
    padding: 0.15rem 0.35rem;
    border-radius: 6px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    flex-shrink: 0;
    margin-left: 0.5rem;
  }

  .iteration-badge {
    background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%);
    color: white;
    font-size: 0.6rem;
    font-weight: 700;
    padding: 0.15rem 0.35rem;
    border-radius: 6px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    flex-shrink: 0;
    margin-left: 0.5rem;
  }

  .metadata-badge {
    background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
    color: white;
    font-size: 0.6rem;
    font-weight: 700;
    padding: 0.15rem 0.35rem;
    border-radius: 6px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    flex-shrink: 0;
    margin-left: 0.5rem;
  }
}
</style>
